import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { Preferences } from '@capacitor/preferences';
import { supabase } from '../lib/supabase';
import { mapError } from '../lib/errors';
import { Spinner } from '../components/ui/Spinner';
import { isNative } from '../lib/platform';
import { useI18n } from '../hooks/useI18n';
import { OAuthButtons, OAuthDivider } from '../components/auth/OAuthButtons';
import {
  isBiometricAvailable,
  isBiometricEnrolled,
  loginWithBiometric,
  saveSessionForBiometric,
  clearBiometricSession,
} from '../lib/native/biometrics';

const BIOMETRIC_PROMPT_KEY = 'biometric_prompt_shown';

export function Login() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { t }     = useI18n();
  const from      = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/dashboard';

  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [visible,  setVisible]  = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [oauthLoading, setOauthLoading] = useState<'google' | 'apple' | null>(null);
  const [error,    setError]    = useState<string | null>(null);
  const [biometricPrompt, setBiometricPrompt] = useState(false);

  // On native: attempt biometric login immediately if enrolled
  useEffect(() => {
    if (!isNative) return;
    isBiometricEnrolled().then(async enrolled => {
      if (!enrolled) return;
      const token = await loginWithBiometric();
      if (token) {
        const { error: authErr } = await supabase.auth.setSession({
          access_token: token,
          refresh_token: token,
        });
        if (!authErr) {
          navigate(from, { replace: true });
        } else {
          // Token expired, clear biometric and show form
          await clearBiometricSession();
        }
      }
    });
  }, [from, navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password });

    if (authError) {
      setError(mapError(authError));
      setLoading(false);
      return;
    }

    // Offer biometric on first successful login (native only)
    if (isNative && data.session) {
      const { value: shown } = await Preferences.get({ key: BIOMETRIC_PROMPT_KEY });
      if (!shown) {
        const available = await isBiometricAvailable();
        if (available) {
          await Preferences.set({ key: BIOMETRIC_PROMPT_KEY, value: 'true' });
          setBiometricPrompt(true);
          setLoading(false);
          return; // prompt modal will handle navigation
        }
      }
    }

    // RT-21: Check if MFA (2FA) is required, redirect to TOTP verify page
    const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (aalData?.nextLevel === 'aal2' && aalData.nextLevel !== aalData.currentLevel) {
      navigate('/login/verify-2fa', { replace: true, state: { from } });
      return;
    }

    navigate(from, { replace: true });
  }

  async function enableBiometric() {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      await saveSessionForBiometric(session.access_token);
    }
    setBiometricPrompt(false);
    navigate(from, { replace: true });
  }

  async function skipBiometric() {
    setBiometricPrompt(false);
    navigate(from, { replace: true });
  }

  async function handleOAuth(provider: 'google' | 'apple') {
    setOauthLoading(provider);
    setError(null);
    const { error: oauthErr } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: `${window.location.origin}${from}` },
    });
    if (oauthErr) {
      setError(t('auth.oauthError'));
      setOauthLoading(null);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'var(--bg-base)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1.5rem',
    }}>
      <div style={{ width: '100%', maxWidth: '420px' }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <Link to="/" style={{ textDecoration: 'none', display: 'inline-block' }}>
            <img src="/logo.svg" alt="Professya" style={{ height: '44px', width: 'auto' }} />
          </Link>
          <p style={{ color: 'var(--text-secondary)', marginTop: '0.75rem', fontSize: 'var(--text-label)' }}>
            {t('auth.signInTitle')}
          </p>
        </div>

        {/* Biometric enable prompt */}
        {biometricPrompt ? (
          <div className="card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🔒</div>
            <h2 className="font-display" style={{ fontSize: '1.25rem', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
              {t('auth.biometricTitle')}
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '1.5rem', lineHeight: 1.6 }}>
              {t('auth.biometricDesc')}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <button className="btn btn-primary btn-block" onClick={enableBiometric}>
                {t('auth.biometricEnable')}
              </button>
              <button className="btn btn-secondary btn-block" onClick={skipBiometric}>
                {t('auth.biometricSkip')}
              </button>
            </div>
          </div>
        ) : (
          <div className="card">
            <form onSubmit={handleSubmit} noValidate>

              {error && (
                <div role="alert" style={{
                  padding: '0.875rem 1rem',
                  backgroundColor: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.3)',
                  borderRadius: 'var(--radius-md)',
                  color: '#fca5a5',
                  fontSize: 'var(--text-label)',
                  marginBottom: '1.25rem',
                }}>
                  {error}
                </div>
              )}

              <div className="form-group">
                <label className="form-label" htmlFor="email">{t('auth.email')}</label>
                <input
                  id="email"
                  type="email"
                  className="form-input"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  autoComplete="email"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="password">{t('auth.password')}</label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="password"
                    type={visible ? 'text' : 'password'}
                    className="form-input"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                    style={{ paddingRight: '3rem' }}
                  />
                  <button
                    type="button"
                    onClick={() => setVisible(v => !v)}
                    aria-label={visible ? t('auth.hidePassword') : t('auth.showPassword')}
                    style={{
                      position: 'absolute', right: '0.875rem', top: '50%',
                      transform: 'translateY(-50%)', background: 'none',
                      border: 'none', cursor: 'pointer', color: 'var(--text-muted)',
                      display: 'flex', alignItems: 'center', padding: '0.25rem',
                    }}
                  >
                    {visible ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <p className="form-hint" style={{ textAlign: 'right' }}>
                  <Link to="/reset-password" style={{ color: 'var(--petrol)', fontSize: 'var(--text-caption)' }}>
                    {t('auth.forgotPassword')}
                  </Link>
                </p>
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-block"
                disabled={loading || !email || !password}
              >
                {loading ? <Spinner size="sm" /> : t('auth.signIn')}
              </button>

            </form>

            {/* OAuth buttons */}
            <OAuthDivider label={t('auth.orContinue')} />
            <OAuthButtons
              onGoogle={() => handleOAuth('google')}
              loadingProvider={oauthLoading}
              labelGoogle={t('auth.continueGoogle')}
            />

            <div className="divider" />

            <p style={{ textAlign: 'center', fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>
              {t('auth.noAccount')}{' '}
              <Link to="/register" style={{ color: 'var(--petrol)', fontWeight: 600 }}>
                {t('auth.createFree')}
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
